import React, { useState, useEffect, useContext, useMemo } from 'react';
import Swal from 'sweetalert2';
import { AuthContext } from '../../AuthContext';
import axiosInstance from '../../axiosConfig';
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import "../../assets/CSS/Dashboard.css";

const penaltyTab = (penalty) => {
  const type = String(penalty?.wip_type || '').trim().toLowerCase();
  if (['supervisor', 'supervisors', 'superviser', 'supervisers'].includes(type) || penalty?.supervisor_id) return 'Supervisors';
  return 'Workers';
};

export default function PenaltyReview() {
  const { user } = useContext(AuthContext);
  const [penalties, setPenalties] = useState([]);
  const [kpiEvents, setKpiEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSidebarVisible, setIsSidebarVisible] = useState(window.innerWidth >= 768);

  // Filters State
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [workerFilter, setWorkerFilter] = useState('');
  const [activeTab, setActiveTab] = useState('Workers');

  const toggleSidebar = () => setIsSidebarVisible(!isSidebarVisible);

  useEffect(() => {
    if (!user) {
      window.location.href = "/";
      return;
    }
    fetchPenalties();
  }, [user]);

  const fetchPenalties = async () => {
    try {
      setLoading(true);
      const [penaltyResult, kpiResult] = await Promise.allSettled([
        axiosInstance.get('/api/wip-dashboard/admin/penalties'),
        axiosInstance.get('/api/wip-dashboard/kpi-events')
      ]);
      if (penaltyResult.status === 'fulfilled' && penaltyResult.value.data.success) {
        setPenalties((penaltyResult.value.data.penalties || []).map(penalty => ({
          ...penalty,
          display_type: penaltyTab(penalty)
        })));
      } else {
        throw penaltyResult.reason || new Error('Penalty API returned an invalid response');
      }
      if (kpiResult.status === 'fulfilled' && kpiResult.value.data.success) setKpiEvents(kpiResult.value.data.events || []);
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Failed to fetch penalty recommendations', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (penalty_id, action) => {
    try {
      const confirmation = await Swal.fire({
        title: 'HR Decision',
        html: `
          <select id="hr-decision" class="swal2-select"><option value="${action}">${action}</option><option value="REDUCED">Reduce amount</option><option value="COACHING">Coaching only</option><option value="RETRAINING">Retraining</option><option value="WARNING">Warning</option><option value="FURTHER_INVESTIGATION">Further investigation</option></select>
          <input id="approved-amount" class="swal2-input" type="number" min="0" placeholder="Approved amount (optional)">
          <input id="payroll-period" class="swal2-input" placeholder="Payroll period, e.g. Aug-2026">
          <input id="hr-attachment" class="swal2-file" type="file" accept="image/*,.pdf">
          <textarea id="employee-explanation" class="swal2-textarea" placeholder="Employee explanation / HR remarks"></textarea>`,
        showCancelButton: true,
        confirmButtonColor: action === 'APPROVED' ? '#d33' : '#3085d6',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Save decision',
        preConfirm: () => ({
          hr_decision: document.getElementById('hr-decision').value,
          approved_amount: document.getElementById('approved-amount').value,
          payroll_period: document.getElementById('payroll-period').value,
          employee_explanation: document.getElementById('employee-explanation').value,
          hr_attachment: document.getElementById('hr-attachment').files[0]
        })
      });

      if (!confirmation.isConfirmed) return;

      const formData = new FormData();
      formData.append('penalty_id', penalty_id);
      formData.append('action', action);
      formData.append('reviewed_by', user.Name || 'HR Admin');
      Object.entries(confirmation.value).forEach(([key, value]) => { if (value !== undefined && value !== null) formData.append(key, value); });
      const res = await axiosInstance.post('/api/wip-dashboard/admin/penalties/action', formData);

      if (res.data.success) {
        Swal.fire('Success', res.data.message, 'success');
        fetchPenalties(); // Refresh list
      } else {
        Swal.fire('Error', res.data.message || 'Action failed', 'error');
      }
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Server error during action', 'error');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const d = new Date(dateString);
    return d.toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  // Derive unique workers for dropdown
  const uniqueWorkers = useMemo(() => {
    const workers = new Map();
    penalties.forEach(p => {
      if (p.worker_id && !workers.has(p.worker_id)) {
        workers.set(p.worker_id, p.worker_name ? p.worker_name.trim() : 'Unknown');
      }
    });
    return Array.from(workers.entries()).map(([id, name]) => ({ id, name }));
  }, [penalties]);

  // Apply Filters
  const filteredPenalties = useMemo(() => {
    return penalties.filter(p => {
      // 1. Search Query
      const q = searchQuery.toLowerCase();
      const matchesSearch = 
        p.lot_id?.toLowerCase().includes(q) || 
        p.reason?.toLowerCase().includes(q) ||
        p.worker_name?.toLowerCase().includes(q) ||
        p.worker_id?.toLowerCase().includes(q);

      // 2. Status Filter
      let matchesStatus = true;
      if (statusFilter !== "ALL") {
        if (statusFilter === "PENDING") matchesStatus = p.status === "PENDING_REVIEW";
        else matchesStatus = p.status === statusFilter;
      }

      // 3. Worker Filter
      let matchesWorker = true;
      if (workerFilter) {
        matchesWorker = p.worker_id === workerFilter;
      }

      // 4. Tab Filter
      const matchesTab = p.display_type === activeTab;

      return matchesSearch && matchesStatus && matchesWorker && matchesTab;
    });
  }, [penalties, searchQuery, statusFilter, workerFilter, activeTab]);

  return (
    <div className="d-flex dashboard">
      {/* ── SIDEBAR ── */}
      <div className={isSidebarVisible ? "sidebar-container shadow-sm" : "sidebar-hidden"}>
        <Sidebar />
      </div>

      <div className="flex-grow-1">
        {/* ── HEADER ── */}
        <Header toggleSidebar={toggleSidebar} isSidebarVisible={isSidebarVisible} />

        {/* ── MAIN CONTENT AREA ── */}
        <main className="main-container p-0 pb-5" style={{ background: "#f8fafc", fontFamily: "'Inter', sans-serif", minHeight: "calc(100vh - 60px)" }}>
          
          {/* PAGE BANNER */}
          <div style={{
            background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
            padding: "40px 48px", color: "#fff",
            position: "relative",
            overflow: "hidden"
          }}>
            <div style={{ position: "absolute", top: -50, right: -50, width: 200, height: 200, background: "linear-gradient(135deg, rgba(245,158,11,0.3) 0%, rgba(220,38,38,0.2) 100%)", borderRadius: "50%", filter: "blur(40px)" }}></div>
            <h1 style={{ margin: 0, fontSize: 32, fontWeight: 800, letterSpacing: "-0.5px", display: "flex", alignItems: "center" }}>
              <div style={{ background: "rgba(245, 158, 11, 0.2)", padding: "10px", borderRadius: "12px", marginRight: "16px" }}>
                <i className="bi bi-currency-rupee text-warning" style={{ fontSize: "24px" }}></i> 
              </div>
              HR Penalty Review
            </h1>
            <p style={{ margin: "12px 0 0 64px", opacity: 0.8, fontSize: 16, fontWeight: 500, maxWidth: "600px" }}>
              Review system-generated penalty recommendations for SLA breaches. Approve or reject deductions before payroll processing.
            </p>
          </div>

          <div className="container-fluid mt-4" style={{ maxWidth: "1400px", padding: "0 40px" }}>
            
            {/* TABS SECTION */}
            <div className="d-flex mb-4" style={{ gap: "10px" }}>
              <button 
                className={`btn ${activeTab === 'Workers' ? 'btn-primary' : 'btn-outline-secondary'}`}
                style={{ borderRadius: "20px", padding: "8px 24px", fontWeight: 600, border: activeTab === 'Workers' ? 'none' : '1px solid #e2e8f0', background: activeTab === 'Workers' ? '#1e293b' : '#fff', color: activeTab === 'Workers' ? '#fff' : '#64748b' }}
                onClick={() => setActiveTab('Workers')}
              >
                <i className="bi bi-people me-2"></i> Workers
              </button>
              <button 
                className={`btn ${activeTab === 'Supervisors' ? 'btn-primary' : 'btn-outline-secondary'}`}
                style={{ borderRadius: "20px", padding: "8px 24px", fontWeight: 600, border: activeTab === 'Supervisors' ? 'none' : '1px solid #e2e8f0', background: activeTab === 'Supervisors' ? '#1e293b' : '#fff', color: activeTab === 'Supervisors' ? '#fff' : '#64748b' }}
                onClick={() => setActiveTab('Supervisors')}
              >
                <i className="bi bi-person-badge me-2"></i> Supervisors
              </button>
            </div>

            {/* FILTERS SECTION */}
            <div className="card border-0 mb-4" style={{ borderRadius: "16px", boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05)", background: "#fff" }}>
              <div className="card-body p-4 d-flex gap-3 flex-wrap">
                <div style={{ flex: "1 1 250px" }}>
                  <label style={{ fontSize: "12px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", marginBottom: "6px", display: "block" }}>Search Records</label>
                  <div className="input-group">
                    <span className="input-group-text bg-white border-end-0"><i className="bi bi-search text-muted"></i></span>
                    <input 
                      type="text" 
                      className="form-control border-start-0 ps-0" 
                      placeholder="Lot ID, Reason, Worker Name or ID..." 
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      style={{ boxShadow: "none" }}
                    />
                  </div>
                </div>

                <div style={{ flex: "1 1 200px" }}>
                  <label style={{ fontSize: "12px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", marginBottom: "6px", display: "block" }}>Filter by Status</label>
                  <select 
                    className="form-select" 
                    value={statusFilter} 
                    onChange={e => setStatusFilter(e.target.value)}
                    style={{ boxShadow: "none" }}
                  >
                    <option value="ALL">All Penalties</option>
                    <option value="PENDING">Pending Review</option>
                    <option value="APPROVED">Approved</option>
                    <option value="REJECTED">Rejected</option>
                  </select>
                </div>

                <div style={{ flex: "1 1 200px" }}>
                  <label style={{ fontSize: "12px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", marginBottom: "6px", display: "block" }}>Filter by Worker</label>
                  <select 
                    className="form-select" 
                    value={workerFilter} 
                    onChange={e => setWorkerFilter(e.target.value)}
                    style={{ boxShadow: "none" }}
                  >
                    <option value="">All Workers</option>
                    {uniqueWorkers.map(w => (
                      <option key={w.id} value={w.id}>{w.name} ({w.id})</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="text-center" style={{ padding: "100px 0" }}>
                <div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }}></div>
                <h5 className="mt-4 text-secondary" style={{ fontWeight: 600 }}>Loading penalty records...</h5>
              </div>
            ) : (
              <div className="card border-0" style={{ borderRadius: "20px", overflow: "hidden", background: "#fff", boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.01)" }}>
                <div className="card-header bg-white border-0" style={{ padding: "30px 30px 20px" }}>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h5 style={{ fontWeight: 800, color: "#0f172a", margin: 0, fontSize: "20px" }}>
                        <i className="bi bi-list-check text-primary me-2"></i> Penalty Recommendations
                      </h5>
                    </div>
                    <span className="badge bg-warning-subtle text-warning" style={{ padding: "8px 16px", borderRadius: "20px", fontWeight: 700 }}>
                      {filteredPenalties.length} Records Found
                    </span>
                  </div>
                </div>
                
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table align-middle mb-0" style={{ fontSize: "15px" }}>
                      <thead style={{ background: "#1e293b" }}>
                        <tr>
                          <th style={{ color: "#ffffff", fontWeight: 700, padding: "16px 30px", border: "none", textTransform: "uppercase", fontSize: "12px", letterSpacing: "0.5px" }}>{activeTab === 'Workers' ? 'Worker Details' : 'Supervisor Details'}</th>
                          <th style={{ color: "#ffffff", fontWeight: 700, padding: "16px 20px", border: "none", textTransform: "uppercase", fontSize: "12px", letterSpacing: "0.5px" }}>Lot / Reason</th>
                          <th style={{ color: "#ffffff", fontWeight: 700, padding: "16px 20px", border: "none", textTransform: "uppercase", fontSize: "12px", letterSpacing: "0.5px" }}>Deduction</th>
                          <th style={{ color: "#ffffff", fontWeight: 700, padding: "16px 20px", border: "none", textTransform: "uppercase", fontSize: "12px", letterSpacing: "0.5px" }}>Generated On</th>
                          <th style={{ color: "#ffffff", fontWeight: 700, padding: "16px 30px", border: "none", textTransform: "uppercase", fontSize: "12px", letterSpacing: "0.5px" }}>Actions / Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredPenalties.length === 0 ? (
                          <tr><td colSpan="5" className="text-center text-muted" style={{ padding: "60px 0" }}>
                            <i className="bi bi-shield-check text-slate-300" style={{ fontSize: "40px", color: "#cbd5e1" }}></i>
                            <div className="mt-3" style={{ fontWeight: 600 }}>No penalty recommendations match your filters.</div>
                          </td></tr>
                        ) : filteredPenalties.map(p => (
                          <tr key={p.penalty_id} style={{ transition: "background 0.2s" }} onMouseOver={e => e.currentTarget.style.background = "#f8fafc"} onMouseOut={e => e.currentTarget.style.background = "transparent"}>
                            <td style={{ padding: "20px 30px", borderBottom: "1px solid #e2e8f0" }}>
                              {activeTab === 'Workers' ? (
                                <>
                                  <div style={{ fontWeight: 700, color: "#1e293b", fontSize: "15px" }}>{p.worker_name || 'Unknown'}</div>
                                  <div style={{ color: "#64748b", fontWeight: 500, fontSize: "13px", marginTop: "4px" }}>ID: {p.worker_id}</div>
                                </>
                              ) : (
                                <>
                                  <div style={{ fontWeight: 700, color: "#1e293b", fontSize: "15px" }}>{p.supervisor_id || 'Unknown'}</div>
                                  <div style={{ color: "#64748b", fontWeight: 500, fontSize: "13px", marginTop: "4px" }}>Supervisor</div>
                                </>
                              )}
                            </td>
                            <td style={{ padding: "20px 20px", borderBottom: "1px solid #e2e8f0" }}>
                              <div style={{ fontWeight: 800, color: "#0f172a" }}>{p.lot_id}</div>
                              <div style={{ color: "#ef4444", fontWeight: 600, fontSize: "13px", marginTop: "4px" }}>{p.reason}</div>
                            </td>
                            <td style={{ padding: "20px 20px", borderBottom: "1px solid #e2e8f0" }}>
                              <div style={{ fontWeight: 800, color: "#d97706", fontSize: "16px", display: "flex", alignItems: "center" }}>
                                <i className="bi bi-currency-rupee me-1"></i> {p.recommended_deduction}
                              </div>
                            </td>
                            <td style={{ padding: "20px 20px", borderBottom: "1px solid #e2e8f0" }}>
                              <div style={{ fontSize: "13px", color: "#475569", fontWeight: 600 }}>{formatDate(p.created_at)}</div>
                            </td>
                            <td style={{ padding: "20px 30px", borderBottom: "1px solid #e2e8f0" }}>
                              {p.status === 'PENDING_REVIEW' ? (
                                <div className="d-flex gap-2">
                                  <button onClick={() => handleAction(p.penalty_id, 'APPROVED')} className="btn btn-sm btn-outline-danger" style={{ fontWeight: 700, borderRadius: "6px", padding: "6px 12px" }}>
                                    <i className="bi bi-check-circle-fill me-1"></i> Approve
                                  </button>
                                  <button onClick={() => handleAction(p.penalty_id, 'REJECTED')} className="btn btn-sm btn-outline-secondary" style={{ fontWeight: 700, borderRadius: "6px", padding: "6px 12px" }}>
                                    <i className="bi bi-x-circle-fill me-1"></i> Reject
                                  </button>
                                </div>
                              ) : (
                                <span style={{ 
                                  display: "inline-block", 
                                  background: p.status === 'APPROVED' ? "#fef2f2" : "#f1f5f9", 
                                  color: p.status === 'APPROVED' ? "#dc2626" : "#475569", 
                                  padding: "6px 12px", borderRadius: "8px", fontWeight: 800, fontSize: "13px" 
                                }}>
                                  {p.status}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
            <div className="card border-0 mt-4" style={{ borderRadius: "16px", overflow: "hidden" }}>
              <div className="card-header bg-white p-4"><h5 className="m-0 fw-bold">KPI Decision History</h5></div>
              <div className="table-responsive"><table className="table mb-0"><thead><tr><th>Person</th><th>Decision/Event</th><th>Points</th><th>Lot</th><th>Created</th></tr></thead><tbody>{kpiEvents.length ? kpiEvents.map(e => <tr key={e.kpi_event_id}><td>{e.person_id}</td><td>{e.event_type}</td><td className={e.points < 0 ? 'text-danger fw-bold' : 'fw-bold'}>{e.points}</td><td>{e.lot_id || '-'}</td><td>{formatDate(e.created_at)}</td></tr>) : <tr><td colSpan="5" className="text-center text-muted py-4">No KPI history found.</td></tr>}</tbody></table></div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
